import { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isLoggingOut } from '@/utils/logout-state';

export interface UseUnsavedChangesWarningOptions {
  hasUnsavedChanges: boolean;
  enabled?: boolean;
  message?: string;
}

/**
 * Hook to warn users about unsaved changes when navigating away
 *
 * Features:
 * - Intercepts link clicks to show confirmation modal (compatible with BrowserRouter)
 * - Warns on tab/window close with browser dialog (via beforeunload)
 * - Handles browser back/forward buttons
 *
 * @param hasUnsavedChanges - Whether there are unsaved changes
 * @param enabled - Whether the warning is enabled (default: true)
 * @param message - Custom message for browser beforeunload (default: generic message)
 */
export function useUnsavedChangesWarning({
  hasUnsavedChanges,
  enabled = true,
  message,
}: UseUnsavedChangesWarningOptions) {
  const [isBlocked, setIsBlocked] = useState(false);
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const shouldBlockRef = useRef(false);

  // Update ref when hasUnsavedChanges changes
  useEffect(() => {
    shouldBlockRef.current = enabled && hasUnsavedChanges;
  }, [enabled, hasUnsavedChanges]);

  // Intercept all link clicks in the document
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      // Check if we should block navigation
      if (!shouldBlockRef.current) {
        return;
      }

      // Find the closest anchor tag or button that might trigger navigation
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (!link) {
        return;
      }

      // Don't intercept download links (e.g., blob URLs for file exports)
      if (link.hasAttribute('download')) {
        return;
      }

      const href = link.getAttribute('href');

      // Only intercept internal links (not external or hash links)
      if (
        href &&
        !href.startsWith('http') &&
        !href.startsWith('mailto:') &&
        !href.startsWith('tel:') &&
        !href.startsWith('blob:') &&
        !href.startsWith('#') &&
        href !== location.pathname &&
        // Don't intercept if it's the same path (just different query params)
        !href.startsWith(location.pathname + '?')
      ) {
        // Prevent default navigation
        event.preventDefault();
        event.stopPropagation();

        // Show confirmation modal
        setPendingHref(href);
        setIsBlocked(true);
      }
    };

    // Add click listener to document (capture phase to intercept early)
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled, location.pathname]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!enabled || !hasUnsavedChanges) {
      return;
    }

    // Push a dummy state to detect back button
    const handlePopState = (_event: PopStateEvent) => {
      if (shouldBlockRef.current) {
        // Push current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        setIsBlocked(true);
      }
    };

    // Push initial state
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, hasUnsavedChanges]);

  // Warn on tab/window close
  useEffect(() => {
    if (!enabled || !hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isLoggingOut()) {
        return;
      }

      event.preventDefault();
      // Modern browsers ignore custom messages and show generic warning
      // But we still need to set returnValue for the dialog to appear
      event.returnValue = message || '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, hasUnsavedChanges, message]);

  // Proceed with blocked navigation
  const proceed = useCallback(() => {
    if (isBlocked) {
      // Temporarily disable blocking
      shouldBlockRef.current = false;
      setIsBlocked(false);

      if (pendingHref) {
        // Navigate to pending href
        navigate(pendingHref);
        setPendingHref(null);
      } else {
        // Back button was clicked, go back
        window.history.back();
      }
    }
  }, [isBlocked, pendingHref, navigate]);

  // Cancel blocked navigation
  const reset = useCallback(() => {
    setIsBlocked(false);
    setPendingHref(null);
  }, []);

  return {
    isBlocked,
    proceed,
    reset,
  };
}
